import React, { useState, useEffect } from 'react';
import { 
  Flex, 
  Box, 
  Text, 
  IconButton, 
  useDisclosure, 
  Stack, 
  Link, 
  Menu, 
  MenuButton, 
  MenuList, 
  MenuItem, 
  Button,
  VStack,
  HStack,
  Icon,
  Divider
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { FaShoppingCart, FaHome, FaQuestionCircle, FaEnvelope, FaInfoCircle, FaBox, FaUser, FaSignOutAlt } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import authService from '../services/authService';
import '../index.css';

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const cartItems = useSelector(state => state.cart.items);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const { isOpen: isCategoriesMenuOpen, onOpen: onCategoriesMenuOpen, onClose: onCategoriesMenuClose } = useDisclosure();
  const { isOpen: isUserMenuOpen, onOpen: onUserMenuOpen, onClose: onUserMenuClose } = useDisclosure();

  // Get user from token on mount and route change
  useEffect(() => {
    const currentUser = authService.getUser();
    setUser(currentUser);
  }, [location.pathname]);

  // Handle logout
  const handleLogout = () => {
    authService.logout();
    setUser(null);
    navigate('/auth');
  };

  // Truncate email for display
  const truncateEmail = (email, maxLength = 20) => {
    if (!email) return 'Unknown User';
    if (email.length <= maxLength) return email;
    return email.substring(0, maxLength - 3) + '...';
  };

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isHomePage) {
      setIsScrolled(false);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const handleScroll = () => {
      const threshold = window.innerWidth < 768 ? 40 : 80;
      setIsScrolled(window.scrollY > threshold);
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage]);

  const isTransparentHome = isHomePage && !isScrolled;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
    <MotionBox 
      position={isHomePage ? "fixed" : "relative"}
      top="0"
      left="0"
      width="100%"
      bg={isTransparentHome ? "transparent" : "white"} 
      px={4} 
      zIndex={isHomePage ? 1000 : 10}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 120, damping: 14 }}
  boxShadow={isTransparentHome ? "none" : "md"}
  borderBottom={isTransparentHome ? "none" : "1px solid rgba(0,0,0,0.08)"}
  backdropFilter={isTransparentHome ? "none" : "blur(12px)"}
      style={{ transition: 'background-color 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease' }}
    >
      <Flex h={{ base: 14, md: 16 }} alignItems="center" justifyContent="space-between">
        <IconButton
          size={{ base: "sm", md: "md" }}
          icon={<HamburgerIcon />}
          aria-label="Open Menu"
          display={isMobileMenuOpen ? 'none' : { base: 'flex', md: 'none' }}
          onClick={toggleMobileMenu}
          zIndex={1001}
          variant="solid"
          color={isTransparentHome ? "white" : "gray.800"}
          bg={isTransparentHome ? "whiteAlpha.200" : "white"}
          border="2px solid"
          borderColor={isTransparentHome ? "whiteAlpha.400" : "black"}
          boxShadow="0 2px 8px rgba(0, 0, 0, 0.15)"
          _hover={{ 
            bg: isTransparentHome ? "whiteAlpha.300" : "gray.50",
            borderColor: isTransparentHome ? "whiteAlpha.500" : "gray.400",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)"
          }}
          _active={{
            bg: isTransparentHome ? "whiteAlpha.400" : "gray.100",
            transform: "scale(0.95)"
          }}
        />
        <Box>
          <Link as={RouterLink} to="/" _hover={{ textDecoration: "none" }}>
            <Text 
              fontSize={{ base: "5xl", md: "6xl" }} 
              fontWeight="800" 
              color={isTransparentHome ? "white" : "gray.800"} 
              fontFamily="'Cormorant', serif"
              letterSpacing="wider"
              textShadow={isTransparentHome ? "2px 2px 4px rgba(0,0,0,0.3)" : "none"}
            >
              AGNIR
            </Text>
          </Link>
        </Box>
        <Flex alignItems="center">
          <Stack key={location.pathname} direction="row" spacing={8} display={{ base: 'none', md: 'flex' }}>
            <MotionButton as={RouterLink} to="/" fontWeight="semibold" bg="transparent"  color={isTransparentHome ? "white" : "gray.800"} border={isTransparentHome ? "1px solid white" : "1px solid gray.800"} px={3} py={1} borderRadius="md" _hover={{ bg: isTransparentHome ? "white" : "gray.800", color: isTransparentHome ? "black" : "white", scale: 1.05, textDecoration: "none" }}>Home</MotionButton>
            <Menu isOpen={isCategoriesMenuOpen} onClose={onCategoriesMenuClose}>
              <MenuButton 
                as={Button} 
                fontWeight="semibold" 
                color={isTransparentHome ? "white" : "gray.800"} 
                border={isTransparentHome ? "1px solid white" : "1px solid gray.800"} 
                px={3} 
                py={1} 
                borderRadius="md" 
                bg="transparent" /* Ensure transparent background by default */
                _hover={{ bg: isTransparentHome ? "white" : "gray.800", color: isTransparentHome ? "black" : "white", scale: 1.05, textDecoration: "none" }}
                _active={{ bg: isTransparentHome ? "white" : "gray.800",color: isTransparentHome ? "black" : "white" }} /* Ensure text color is readable when active */
                onMouseEnter={onCategoriesMenuOpen}
                onMouseLeave={onCategoriesMenuClose}
              >
                Products
              </MenuButton>
              <MenuList 
                bg={isTransparentHome ? "rgba(230, 224, 224, 0.8)" : "white"} 
                borderColor={isTransparentHome ? "white" : "gray.200"}
                onMouseEnter={onCategoriesMenuOpen}
                onMouseLeave={onCategoriesMenuClose}
              >
                <MenuItem as={RouterLink} to="/products/rings" color="gray.800" _hover={{ bg: isTransparentHome ? "gray.200" : "gray.100" }}>Rings</MenuItem>
                <MenuItem as={RouterLink} to="/products/bracelets" color="gray.800" _hover={{ bg: isTransparentHome ? "gray.200" : "gray.100" }}>Bracelets</MenuItem>
                <MenuItem as={RouterLink} to="/products/pendants" color="gray.800" _hover={{ bg: isTransparentHome ? "gray.200" : "gray.100" }}>Pendants</MenuItem>
                <MenuItem as={RouterLink} to="/products/chains" color="gray.800" _hover={{ bg: isTransparentHome ? "gray.200" : "gray.100" }}>Chains</MenuItem>
              </MenuList>
            </Menu>
            <MotionButton as={RouterLink} to="/faqs" fontWeight="semibold" bg="transparent" color={isTransparentHome ? "white" : "gray.800"} border={isTransparentHome ? "1px solid white" : "1px solid gray.800"} px={3} py={1} borderRadius="md" _hover={{ bg: isTransparentHome ? "white" : "gray.800", color: isTransparentHome ? "black" : "white", scale: 1.05, textDecoration: "none" }}>FAQs</MotionButton>
            <MotionButton as={RouterLink} to="/contact" fontWeight="semibold" bg="transparent" color={isTransparentHome ? "white" : "gray.800"} border={isTransparentHome ? "1px solid white" : "1px solid gray.800"} px={3} py={1} borderRadius="md" _hover={{ bg: isTransparentHome ? "white" : "gray.800", color: isTransparentHome ? "black" : "white", scale: 1.05, textDecoration: "none" }}>Contact</MotionButton>
            {/* <MotionButton as={RouterLink} to="/about-us" fontWeight="semibold" bg="transparent" color={isTransparentHome ? "white" : "gray.800"} border={isTransparentHome ? "1px solid white" : "1px solid gray.800"} px={3} py={1} borderRadius="md" _hover={{ bg: isTransparentHome ? "white" : "gray.800", color: isTransparentHome ? "black" : "white", scale: 1.05, textDecoration: "none" }}>About Us</MotionButton> */}
            <MotionButton as={RouterLink} to="/orders" fontWeight="semibold" bg="transparent" color={isTransparentHome ? "white" : "gray.800"} border={isTransparentHome ? "1px solid white" : "1px solid gray.800"} px={3} py={1} borderRadius="md" _hover={{ bg: isTransparentHome ? "white" : "gray.800", color: isTransparentHome ? "black" : "white", scale: 1.05, textDecoration: "none" }}>Orders</MotionButton>
          </Stack>

          {/* User Section - Desktop */}
          <Box ml={{ base: 2, md: 6 }} display={{ base: 'none', md: 'block' }}>
            {user ? (
              <Menu isOpen={isUserMenuOpen} onClose={onUserMenuClose}>
                <MenuButton
                  as={Box}
                  cursor="pointer"
                  onMouseEnter={onUserMenuOpen}
                  onMouseLeave={onUserMenuClose}
                  px={4}
                  py={2}
                  borderRadius="md"
                  transition="all 0.2s"
                  border={isTransparentHome ? "1px solid rgba(255,255,255,0.3)" : "1px solid rgba(0,0,0,0.1)"}
                  _hover={{
                    bg: isTransparentHome ? "whiteAlpha.200" : "gray.50",
                    borderColor: isTransparentHome ? "whiteAlpha.500" : "gray.300"
                  }}
                >
                  <HStack spacing={2}>
                    <Icon 
                      as={FaUser} 
                      boxSize={4} 
                      color={isTransparentHome ? "white" : "gray.700"}
                    />
                    <VStack spacing={0} align="flex-start" lineHeight="1.2">
                      <Text 
                        fontSize="xs" 
                        color={isTransparentHome ? "whiteAlpha.900" : "gray.600"}
                        fontWeight="bold"
                        letterSpacing="wide"
                      >
                        HELLO
                      </Text>
                      <HStack spacing={1}>
                        <Text 
                          fontSize="sm" 
                          fontWeight="semibold" 
                          color={isTransparentHome ? "white" : "gray.800"}
                        >
                          {truncateEmail(user.email, 18)}
                        </Text>
                        <ChevronDownIcon 
                          boxSize={3}
                          color={isTransparentHome ? "white" : "gray.600"} 
                        />
                      </HStack>
                    </VStack>
                  </HStack>
                </MenuButton>
                <MenuList
                  bg={isTransparentHome ? "rgba(230, 224, 224, 0.95)" : "white"}
                  borderColor={isTransparentHome ? "white" : "gray.200"}
                  onMouseEnter={onUserMenuOpen}
                  onMouseLeave={onUserMenuClose}
                  minW="180px"
                >
                  <MenuItem
                    icon={<Icon as={FaSignOutAlt} />}
                    onClick={handleLogout}
                    color="gray.800"
                    _hover={{ bg: isTransparentHome ? "gray.200" : "gray.100" }}
                  >
                    Logout
                  </MenuItem>
                </MenuList>
              </Menu>
            ) : (
              <MotionButton
                as={RouterLink}
                to="/auth"
                fontWeight="semibold"
                bg="transparent"
                color={isTransparentHome ? "white" : "gray.800"}
                border={isTransparentHome ? "1px solid white" : "1px solid gray.800"}
                px={3}
                py={1}
                borderRadius="md"
                _hover={{
                  bg: isTransparentHome ? "white" : "gray.800",
                  color: isTransparentHome ? "black" : "white",
                  scale: 1.05,
                  textDecoration: "none"
                }}
              >
                Login
              </MotionButton>
            )}
          </Box>

          <Box ml={{ base: 2, md: 4 }} position="relative">
            <Link as={RouterLink} to="/cart" color={isTransparentHome ? "white" : "gray.800"}>
              <Box as={FaShoppingCart} boxSize={{ base: "26px", md: "24px" }} />
              {cartItems.length > 0 && (
                <Text
                  position="absolute"
                  top={{ base: "-6px", md: "-8px" }}
                  right={{ base: "-6px", md: "-8px" }}
                  bg="red.500"
                  color="white"
                  borderRadius="full"
                  px={{ base: 1.5, md: 2 }}
                  fontSize={{ base: "2xs", md: "xs" }}
                  minW={{ base: "16px", md: "20px" }}
                  h={{ base: "16px", md: "20px" }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {cartItems.length}
                </Text>
              )}
            </Link>
          </Box>
        </Flex>
      </Flex>
    </MotionBox>

      {/* Custom Mobile Sidebar (No Chakra Drawer) */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Overlay */}
            <MotionBox
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              position="fixed"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="rgba(0, 0, 0, 0.6)"
              backdropFilter="blur(4px)"
              zIndex={999}
              display={{ base: 'block', md: 'none' }}
              onClick={closeMobileMenu}
            />

            {/* Sidebar */}
            <MotionBox
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              position="fixed"
              top="0"
              left="0"
              bottom="0"
              width="300px"
              maxW="85vw"
              bg="linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
              color="white"
              zIndex={1000}
              overflowY="auto"
              display={{ base: 'block', md: 'none' }}
              boxShadow="2xl"
            >
              {/* Close Button */}
              <IconButton
                icon={<CloseIcon />}
                aria-label="Close Menu"
                onClick={closeMobileMenu}
                position="absolute"
                top={4}
                right={4}
                size="sm"
                variant="ghost"
                color="white"
                zIndex={1}
                _hover={{ 
                  bg: "whiteAlpha.200",
                  transform: "rotate(90deg)"
                }}
                _active={{ 
                  bg: "whiteAlpha.300"
                }}
                transition="all 0.2s ease"
              />

              {/* Header */}
              <Box
                borderBottomWidth="1px"
                borderBottomColor="whiteAlpha.200"
                pb={3}
                pt={5}
                px={5}
                pr={12}
              >
                <HStack spacing={3}>
                  <Text 
                    fontSize="2xl" 
                    fontWeight="800" 
                    fontFamily="'Cormorant', serif"
                    letterSpacing="wider"
                  >
                    AGNIR
                  </Text>
                </HStack>
                <Text 
                  fontSize="xs" 
                  color="whiteAlpha.700" 
                  mt={1}
                  fontFamily="'Inter', sans-serif"
                >
                  For the ones who wear presence
                </Text>
              </Box>

              {/* User Section - Mobile */}
              <Box
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderBottomColor="whiteAlpha.200"
              >
                {user ? (
                  <VStack spacing={2} align="stretch">
                    <HStack spacing={2}>
                      <Icon as={FaUser} boxSize={4} color="whiteAlpha.800" />
                      <Text fontSize="xs" color="whiteAlpha.700" fontWeight="bold">HELLO,</Text>
                      <Text fontSize="xs" fontWeight="semibold" color="white" isTruncated maxW="150px">
                        {truncateEmail(user.email, 25)}
                      </Text>
                    </HStack>
                    <Button
                      leftIcon={<Icon as={FaSignOutAlt} />}
                      onClick={() => {
                        handleLogout();
                        closeMobileMenu();
                      }}
                      size="xs"
                      variant="outline"
                      colorScheme="whiteAlpha"
                      color="white"
                      borderColor="whiteAlpha.300"
                      _hover={{ bg: "whiteAlpha.200" }}
                      width="full"
                    >
                      Logout
                    </Button>
                  </VStack>
                ) : (
                  <Button
                    as={RouterLink}
                    to="/auth"
                    onClick={closeMobileMenu}
                    leftIcon={<Icon as={FaUser} />}
                    width="full"
                    size="sm"
                    variant="outline"
                    colorScheme="whiteAlpha"
                    color="white"
                    borderColor="whiteAlpha.300"
                    _hover={{ bg: "whiteAlpha.200" }}
                  >
                    Login
                  </Button>
                )}
              </Box>

              {/* Body */}
              <Box pt={3} pb={4} px={3}>
                <VStack spacing={0} align="stretch">
                  {/* Main Navigation Links */}
                  <Box mb={2}>
                    <Text 
                      fontSize="2xs" 
                      fontWeight="600" 
                      color="whiteAlpha.600" 
                      mb={2}
                      px={3}
                      fontFamily="'Inter', sans-serif"
                      letterSpacing="wider"
                      textTransform="uppercase"
                    >
                      Navigation
                    </Text>
                    
                    {[
                      { icon: FaHome, label: 'Home', href: '/' },
                      // { icon: FaInfoCircle, label: 'About Us', href: '/about-us' },
                      { icon: FaQuestionCircle, label: 'FAQs', href: '/faqs' },
                      { icon: FaEnvelope, label: 'Contact', href: '/contact' },
                      { icon: FaBox, label: 'Orders', href: '/orders' }
                    ].map((item) => (
                      <MotionBox
                        key={item.label}
                        whileHover={{ x: 8 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Link
                          as={RouterLink}
                          to={item.href}
                          onClick={closeMobileMenu}
                          _hover={{ textDecoration: 'none' }}
                        >
                          <HStack
                            spacing={3}
                            p={3}
                            borderRadius="lg"
                            transition="all 0.3s ease"
                            _hover={{
                              bg: "whiteAlpha.100",
                              boxShadow: "0 4px 12px rgba(255, 255, 255, 0.1)"
                            }}
                          >
                            <Icon as={item.icon} boxSize={4} color="whiteAlpha.800" />
                            <Text 
                              fontWeight="500" 
                              fontSize="sm"
                              fontFamily="'Inter', sans-serif"
                            >
                              {item.label}
                            </Text>
                          </HStack>
                        </Link>
                      </MotionBox>
                    ))}
                  </Box>

                  <Divider borderColor="whiteAlpha.200" my={2} />

                  {/* Products Section */}
                  <Box>
                    <Text 
                      fontSize="2xs" 
                      fontWeight="600" 
                      color="whiteAlpha.600" 
                      mb={2}
                      px={3}
                      fontFamily="'Inter', sans-serif"
                      letterSpacing="wider"
                      textTransform="uppercase"
                    >
                      Collections
                    </Text>
                    
                    {[
                      { label: 'Rings', href: '/products/rings' },
                      { label: 'Bracelets', href: '/products/bracelets' },
                      { label: 'Pendants', href: '/products/pendants' },
                      { label: 'Chains', href: '/products/chains' }
                    ].map((item) => (
                      <MotionBox
                        key={item.label}
                        whileHover={{ x: 8 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Link
                          as={RouterLink}
                          to={item.href}
                          onClick={closeMobileMenu}
                          _hover={{ textDecoration: 'none' }}
                        >
                          <HStack
                            spacing={3}
                            p={3}
                            borderRadius="lg"
                            transition="all 0.3s ease"
                            _hover={{
                              bg: "whiteAlpha.100",
                              boxShadow: "0 4px 12px rgba(255, 255, 255, 0.1)"
                            }}
                          >
                            <Box
                              w={4}
                              h={4}
                              borderRadius="full"
                              bg="whiteAlpha.200"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Box w={1.5} h={1.5} borderRadius="full" bg="white" />
                            </Box>
                            <Text 
                              fontWeight="500" 
                              fontSize="sm"
                              fontFamily="'Inter', sans-serif"
                            >
                              {item.label}
                            </Text>
                          </HStack>
                        </Link>
                      </MotionBox>
                    ))}
                  </Box>

                  <Divider borderColor="whiteAlpha.200" my={2} />

                  {/* Cart Section */}
                  <MotionBox
                    whileHover={{ x: 8 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Link
                      as={RouterLink}
                      to="/cart"
                      onClick={closeMobileMenu}
                      _hover={{ textDecoration: 'none' }}
                    >
                      <HStack
                        spacing={3}
                        p={3}
                        borderRadius="lg"
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        transition="all 0.3s ease"
                        _hover={{
                          bg: "whiteAlpha.200",
                          borderColor: "whiteAlpha.300",
                          boxShadow: "0 4px 12px rgba(255, 255, 255, 0.1)"
                        }}
                      >
                        <Box position="relative">
                          <Icon as={FaShoppingCart} boxSize={4} color="white" />
                          {cartItems.length > 0 && (
                            <Box
                              position="absolute"
                              top="-6px"
                              right="-6px"
                              bg="red.400"
                              color="white"
                              borderRadius="full"
                              minW="16px"
                              h="16px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="2xs"
                              fontWeight="bold"
                            >
                              {cartItems.length}
                            </Box>
                          )}
                        </Box>
                        <VStack spacing={0} align="start">
                          <Text 
                            fontWeight="600" 
                            fontSize="sm"
                            fontFamily="'Inter', sans-serif"
                          >
                            Shopping Cart
                          </Text>
                          <Text 
                            fontSize="2xs" 
                            color="whiteAlpha.700"
                            fontFamily="'Inter', sans-serif"
                          >
                            {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
                          </Text>
                        </VStack>
                      </HStack>
                    </Link>
                  </MotionBox>
                </VStack>
              </Box>
            </MotionBox>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;